import { create } from "zustand";
import {
  getAllEmployees,
  getEmployeeById,
  addEmployee,
  updateEmployee,
  deleteEmployee,
  getAllDepartments,
  getEmployeeProfile
} from "@/services/HrService/EmployeesService";

const useEmployeesStore = create((set, get) => ({
  employees: null,
  selectedEmployee: null,
  employeeProfile: null,
  departments: [],
  loading: false,
  profileLoading: false,
  error: null,
  pagination: null,
  currentPage: 1,
  pageSize: 15,
  filters: {
    status: '',
    search: ''
  },

  // Fetch all employees with pagination
  fetchEmployees: async (page = 1, limit = 15) => {
    try {
      set({ loading: true, error: null });
      const { filters } = get();
      const response = await getAllEmployees(page, limit, filters);
      console.log("Employees API Response:", response);

      if (response.success) {
        set({
          employees: response.data,
          pagination: response.pagination,
          currentPage: page,
          pageSize: limit,
          loading: false
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch employees', loading: false });
      }
    } catch (error) {
      console.error("Fetch Employees Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Change page
  setPage: async (page) => {
    const { pageSize } = get();
    await get().fetchEmployees(page, pageSize);
  },

  // Change page size
  setPageSize: async (size) => {
    await get().fetchEmployees(1, size);
  },

  // Fetch single employee
  fetchEmployeeById: async (id) => {
    try {
      set({ loading: true, error: null });
      const response = await getEmployeeById(id);
      console.log("Employee API Response:", response);
      
      if (response.success) {
        set({ selectedEmployee: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch employee', loading: false });
      }
    } catch (error) {
      console.error("Fetch Employee Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Add new employee
  addNewEmployee: async (employeeData) => {
    try {
      set({ loading: true, error: null });
      const response = await addEmployee(employeeData);
      console.log("Add Employee API Response:", response);

      if (response.success) {
        // Refetch employees from page 1 to get updated paginated list
        await get().fetchEmployees(1, get().pageSize);
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to add employee', loading: false });
        throw new Error(response.error?.message || 'Failed to add employee');
      }
    } catch (error) {
      console.error("Add Employee Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Update employee
  updateEmployeeData: async (id, employeeData) => {
    try {
      set({ loading: true, error: null });
      const response = await updateEmployee(id, employeeData);
      console.log("Update Employee API Response:", response);

      if (response.success) {
        // Refetch current page to get updated data
        const { currentPage, pageSize } = get();
        await get().fetchEmployees(currentPage, pageSize);
        set({ selectedEmployee: response.data });
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to update employee', loading: false });
        throw new Error(response.error?.message || 'Failed to update employee');
      }
    } catch (error) {
      console.error("Update Employee Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Delete employee
  deleteEmployeeById: async (id) => {
    try {
      set({ loading: true, error: null });
      const response = await deleteEmployee(id);
      console.log("Delete Employee API Response:", response);

      if (response.success) {
        // Refetch current page to get updated paginated list
        const { currentPage, pageSize } = get();
        await get().fetchEmployees(currentPage, pageSize);
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to delete employee', loading: false });
        throw new Error(response.error?.message || 'Failed to delete employee');
      }
    } catch (error) {
      console.error("Delete Employee Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch all departments
  fetchDepartments: async () => {
    try {
      const response = await getAllDepartments();
      console.log("Departments API Response:", response);

      if (response.success) {
        set({ departments: response.data || [] });
      } else {
        console.error('Failed to fetch departments:', response.error?.message);
      }
    } catch (error) {
      console.error("Fetch Departments Error:", error);
    }
  },



  // Set filters and refetch
  setFilters: async (newFilters) => {
    set({ filters: { ...get().filters, ...newFilters } });
    // Refetch from page 1 when filters change
    await get().fetchEmployees(1, get().pageSize);
  },

  // Clear filters and refetch
  clearFilters: async () => {
    set({ filters: { status: '', search: '' } });
    // Refetch from page 1 when filters are cleared
    await get().fetchEmployees(1, get().pageSize);
  },

  // Clear selected employee
  clearSelectedEmployee: () => {
    set({ selectedEmployee: null });
  },

  // Reset errors
  resetErrors: () => set({ error: null }),

  // Fetch employee profile with hierarchy
  fetchEmployeeProfile: async (id) => {
    try {
      set({ profileLoading: true, error: null });
      const response = await getEmployeeProfile(id);
      console.log("Employee Profile API Response:", response);

      if (response.success) {
        set({ employeeProfile: response.data, profileLoading: false });
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to fetch employee profile', profileLoading: false });
        throw new Error(response.error?.message || 'Failed to fetch employee profile');
      }
    } catch (error) {
      console.error("Fetch Employee Profile Error:", error);
      set({ error: error.message, profileLoading: false });
      throw error;
    }
  },

  // Clear employee profile
  clearEmployeeProfile: () => {
    set({ employeeProfile: null });
  },
}));

export default useEmployeesStore;